import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Coffee, Sparkles, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function BedtimeRitualGuide() {
  const navigate = useNavigate();

  const sampleRitual = [
    { time: "30 min before", activity: "Make herbal tea (chamomile, lavender, or sleepy-time blend)", why: "Warm drinks and herbs signal relaxation" },
    { time: "25 min before", activity: "Warm shower or bath", why: "Body temperature drop afterward triggers sleepiness" },
    { time: "15 min before", activity: "Skincare routine—make it mindful, not rushed", why: "Self-care ritual signals transition to rest" },
    { time: "10 min before", activity: "Light reading (physical book, not screen)", why: "Engages mind gently without stimulation" },
    { time: "5 min before", activity: "Deep breathing or gratitude reflection", why: "Calms nervous system, clears racing thoughts" },
    { time: "Bedtime", activity: "Lights out, comfortable position, let go", why: "Consistent endpoint trains your brain" }
  ];

  const ritualElements = [
    { element: "Scent", ideas: "Lavender pillow spray, essential oil diffuser, scented candle (extinguish before bed)" },
    { element: "Sound", ideas: "Calming playlist, nature sounds, silence, white noise machine" },
    { element: "Temperature", ideas: "Warm bath then cool room, breathable pajamas, fan if needed" },
    { element: "Comfort", ideas: "Favorite blanket, soft sheets, supportive pillow" },
    { element: "Closure", ideas: "Brief journal entry, tomorrow's to-do list, gratitude reflection" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold mb-4">
            Week 5 • Day 4 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Bedtime Ritual Creation
          </h1>
          <p className="text-xl text-secondary-600">Design Your Perfect Wind-Down Routine</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Moon className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Why Rituals Work</h3>
              <p className="text-rose-100 text-lg">
                A bedtime ritual signals to your brain that sleep is coming. Over time, these cues become automatic triggers 
                for relaxation. Research shows a consistent routine can improve sleep quality by up to 40%.
              </p>
            </div>
          </div>
        </section>

        {/* Sample Ritual */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6 text-rose-500" />
            Sample 30-Minute Ritual
          </h2>
          <div className="space-y-4">
            {sampleRitual.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-rose-50 rounded-xl">
                <span className="font-bold text-rose-600 w-24 flex-shrink-0 text-sm">{item.time}</span>
                <div>
                  <p className="font-medium text-secondary-900">{item.activity}</p>
                  <p className="text-sm text-secondary-500">{item.why}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Build Your Own */}
        <section className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border-2 border-rose-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-rose-500" />
            Build Your Ritual
          </h2>
          <p className="text-secondary-600 mb-4">Consider including elements that engage your senses:</p>
          <div className="space-y-3">
            {ritualElements.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-rose-700 mb-1">{item.element}</h3>
                <p className="text-sm text-secondary-600">{item.ideas}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Tonight */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Tonight's Practice</h2>
          <p className="text-secondary-700 mb-4">
            Design your personal 30-minute wind-down routine. Write it down and try it tonight. 
            Notice how you feel as you go through each step.
          </p>
          <div className="bg-rose-50 rounded-xl p-4">
            <p className="font-medium text-rose-700 mb-2">My Bedtime Ritual:</p>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(n => (
                <div key={n} className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-rose-200 rounded-full flex items-center justify-center text-xs text-rose-700">{n}</span>
                  <div className="flex-1 h-8 border-b border-rose-200"></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default BedtimeRitualGuide;
